import React from 'react';
import { useEffect, useState } from 'react';
import { Modal, ModalActions, ModalNote } from '../../ui/Modal/Modal';
import { Button } from '../../ui/Button/Button';
import { StatusBadge } from '../../ui/StatusBadge/StatusBadge';
import { PriorityTag } from '../../ui/PriorityTag/PriorityTag';
import { CopyIdButton } from '../CopyIdButton/CopyIdButton';
import { agoLabel, formatDateTime } from '../../../utils/format';
import type { SupportRequest } from '../../../entities/supportRequests/model/types';
import styles from './RequestModal.module.scss';

export interface RequestModalProps {
    request: SupportRequest | null;
    onClose: () => void;
    onSend: (id: string, text: string) => void;
    onComplete: (id: string) => void;
}

export function RequestModal({ request, onClose, onSend, onComplete }: RequestModalProps) {
    const [answer, setAnswer] = useState('');

    useEffect(() => setAnswer(''), [request?.id]);

    if (!request) return null;

    const meta: Array<[string, string]> = [
        ['Компания', request.org || 'не указана'],
        ['ИНН', request.inn || '—'],
        ['Автор обращения', request.email],
        ['Создан', formatDateTime(request.createdAt)],
        ['В работе', agoLabel(request.createdAt)],
        ['Ответов', request.replies.length ? String(request.replies.length) : 'нет'],
    ];

    const trimmed = answer.trim();
    const isDone = request.status === 'done';

    const headerExtra = (
        <div className={styles.badges}>
            <StatusBadge status={request.status} />
            <PriorityTag priority={request.priority} withLabel />
            <CopyIdButton id={request.id} />
        </div>
    );

    const footer = (
        <>
            <ModalNote>
                {isDone ? 'Запрос завершён — ответ уйдёт на ' : 'Ответ уйдёт на '}
                {request.email}
            </ModalNote>
            <ModalActions>
                <Button onClick={() => onComplete(request.id)} disabled={isDone}>
                    Завершить запрос
                </Button>
                <Button
                    variant="primary"
                    onClick={() => onSend(request.id, trimmed)}
                    disabled={trimmed.length === 0}
                >
                    Отправить ответ
                </Button>
            </ModalActions>
        </>
    );

    return (
        <Modal
            open
            onClose={onClose}
            width={720}
            title={request.theme}
            description={`Обращение ${request.id}`}
            headerExtra={headerExtra}
            footer={footer}
        >
            <div className={styles.meta}>
                {meta.map(([label, value]) => (
                    <div key={label} className={styles.metaCell}>
                        <div className={styles.metaLabel}>{label}</div>
                        <div className={styles.metaValue}>{value}</div>
                    </div>
                ))}
            </div>

            <div className={styles.blockLabel}>Текст обращения</div>
            <p className={styles.message}>{request.text}</p>

            <div className={styles.blockLabel}>Переписка</div>
            {request.replies.length ? (
                <div className={styles.thread}>
                    {request.replies.map((reply) => (
                        <article key={reply.id} className={styles.reply}>
                            <div className={styles.replyTop}>
                                <span className={styles.replyWho}>{reply.author}</span>
                                <span className={styles.replyWhen}>{formatDateTime(reply.date)}</span>
                            </div>
                            <div className={styles.replyText}>{reply.text}</div>
                        </article>
                    ))}
                </div>
            ) : (
                <div className={styles.threadEmpty}>
                    Ответов пока нет. Пользователь ждёт {agoLabel(request.createdAt).replace(' назад', '')}.
                </div>
            )}

            <label className={styles.blockLabel} htmlFor="request-answer">
                Ответ пользователю
            </label>
            <textarea
                id="request-answer"
                className={styles.answer}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Опишите решение: что проверили, что сделали, что нужно от пользователя."
            />
        </Modal>
    );
}